import React, { useEffect, useRef, useState, useContext } from 'react';
import NextLink from 'next/link';
import { convertColorToThemeHexCode } from 'Styles/colors.util';
import * as Shared from '../../ContentSystem.shared.styles';
import * as Styled from './TextSet.styled';
import TextToJSX from '../TextToJSX/TextToJSX';
import SecondaryHeader from '../SecondaryHeader/SecondaryHeader';
import Header from '../Header/Header';
import { getClosestLabel } from '../../utils/utils';
import { ContainerQueryContext } from '../../../../util/ContainerQuery.context';
var DEFAULT_ORDERED_ELEMENTS = [
    { type: 'icon' },
    { type: 'primaryHeader' },
    { type: 'secondaryHeader' },
    { type: 'subheader' },
    { type: 'cta' }
];
export var CardTextSet = function (_a) {
    var _b;
    var _c = _a.backgroundColor, backgroundColor = _c === void 0 ? 'white' : _c, cardType = _a.cardType, _d = _a.className, className = _d === void 0 ? undefined : _d, _e = _a.content, content = _e === void 0 ? {
        ctas: []
    } : _e, ctaLayout = _a.ctaLayout, _f = _a.customBackgroundColor, customBackgroundColor = _f === void 0 ? undefined : _f, headerMaxLineLength = _a.headerMaxLineLength, _g = _a.responsiveSizes, responsiveSizes = _g === void 0 ? undefined : _g, textAlignment = _a.textAlignment, _h = _a.textColor, textColor = _h === void 0 ? 'AUTO' : _h, invertImageTextColor = _a.invertImageTextColor, _j = _a.orderedElements, orderedElements = _j === void 0 ? DEFAULT_ORDERED_ELEMENTS : _j;
    var ref = useRef(null);
    var _k = useState(null), dataLabel = _k[0], setDataLabel = _k[1];
    var styleQueryMode = useContext(ContainerQueryContext).styleQueryMode;
    useEffect(function () {
        var parentLabel = getClosestLabel(ref);
        setDataLabel(parentLabel);
    }, []);
    var autoColor = textColor === 'AUTO'
        ? Shared.getCompliantColorTheme({
            textColor: textColor,
            backgroundColor: customBackgroundColor || convertColorToThemeHexCode(backgroundColor)
        })
        : null;
    // Decide whether to pass a "light" text color (white) to children.
    // - autoColor/textColor of 'LIGHT' => light text is needed (for dark backgrounds).
    // - invertImageTextColor flips that decision (useful when an image needs opposite contrast).
    // lightHexCode is either white (force light text) or undefined (use default/dark text).
    var lightHexCode;
    if (invertImageTextColor) {
        // Inverted: if we'd normally use light text, use default; otherwise force light (white).
        lightHexCode =
            autoColor === 'LIGHT' || textColor === 'LIGHT'
                ? undefined
                : convertColorToThemeHexCode('white');
    }
    else {
        // Normal: if theme says "LIGHT", force light (white) text; otherwise use default.
        lightHexCode =
            autoColor === 'LIGHT' || textColor === 'LIGHT'
                ? convertColorToThemeHexCode('white')
                : undefined;
    }
    var ButtonVariant = 'default';
    var isLightText = autoColor === 'LIGHT' || textColor === 'LIGHT';
    var shouldUseDayMode = invertImageTextColor ? !isLightText : isLightText;
    var ButtonColorMode = shouldUseDayMode ? 'day' : 'night';
    var hasCTAs = !!((_b = content.ctas) === null || _b === void 0 ? void 0 : _b.some(function (_a) {
        var text = _a.text;
        return text;
    }));
    var handleOnClick = function (e, url) {
        if (!url || url.includes('[...')) {
            e.preventDefault(); // enable Card CTA overlays open, avoids Next dynamic url overrides
            return;
        }
        e.stopPropagation(); // To prevent CTA clicks interferring with Card's Onclick event listners
    };
    // Get ordered elements based on position hierarchy
    var renderElement = function (element, index) {
        var _a;
        switch (element.type) {
            case 'icon':
                return (content === null || content === void 0 ? void 0 : content.icon) ? (React.createElement(Shared.Icon, { key: "icon-".concat(index), src: content.icon.file.url })) : null;
            case 'primaryHeader':
                return (content === null || content === void 0 ? void 0 : content.header) ? (React.createElement(Header, { key: "primary-header-".concat(index), lightHexCode: lightHexCode }, ctaLayout === 'ALL_ON_CARD' ? (React.createElement(React.Fragment, null, content.header.toString())) : (React.createElement("span", { role: "button", tabIndex: 0 }, content.header.toString())))) : null;
            case 'secondaryHeader':
                return (content === null || content === void 0 ? void 0 : content.secondaryHeader) ? (React.createElement(SecondaryHeader, { key: "secondary-header-".concat(index), lightHexCode: lightHexCode }, content.secondaryHeader)) : null;
            case 'subheader':
                return (content === null || content === void 0 ? void 0 : content.body) ? (React.createElement(TextToJSX, { key: "subheader-".concat(index), text: content.body, lightHexCode: lightHexCode })) : null;
            case 'cta':
                return ((_a = content.ctas) === null || _a === void 0 ? void 0 : _a.filter(function (_a) {
                    var text = _a.text;
                    return text;
                }).length) ? (React.createElement(Shared.CTAGroup, { key: "cta-group-".concat(index), buttonOverflow: "WRAP", cardType: cardType }, content.ctas.map(function (_a, i) {
                    var url = _a.url, text = _a.text, _b = _a.ctaStyle, ctaStyle = _b === void 0 ? 'LINK' : _b, displayLinked = _a.displayLinked, as = _a.as;
                    return (React.createElement(NextLink, { href: url && text ? url : '', key: "".concat(url).concat(text).concat(i), target: displayLinked === 'NEW_TAB' ? '_blank' : '_self', rel: displayLinked ? 'noopener noreferrer' : undefined, onClick: function (e) { return handleOnClick(e, url); } },
                        React.createElement(Shared.CTA, { as: as, "data-label": "".concat(dataLabel, "_CTA").concat(i + 1), responsiveSizes: responsiveSizes, key: "".concat(url).concat(text).concat(i), lightHexCode: lightHexCode, variant: ButtonVariant, size: cardType === 'CONTENT_CARD_MEGA' ? 'medium' : 'small', ctaStyle: ctaStyle, colorMode: ButtonColorMode, "$styleQueryMode": styleQueryMode }, text)));
                }))) : null;
            default:
                return null;
        }
    };
    return (React.createElement(Styled.TextSet, { ref: ref, hasCTAs: hasCTAs, textAlignment: textAlignment, headerMaxLineLength: headerMaxLineLength, className: className, elementType: "card" }, orderedElements.map(function (element, index) { return renderElement(element, index); })));
};
export default {};
